import { defineComponent } from 'vue';
import {
    QBtn, QIcon, QSpinner, QBanner, QDialog, QCard, QCardSection, QCardActions,
    QSelect, QBar, QToolbarTitle, QSpace, QSeparator, QInput, QMenu, QList, QItem, QItemSection, QTooltip,
} from 'quasar';
import { AgGridVue } from 'ag-grid-vue3';
import utils from '../../../../../Shared/utils.jsx';
import FlowV2ListToolbar from './flowv2ListToolbar.jsx';
import {
    aliasClassLabel,
    classNameFromAlias,
    defaultClassName,
    generatedClassFullName,
    listClassesForRow,
    preferredUnusedClassName,
} from './siblingAliasDisplay.js';
import { buildImportTreeRows, defaultImportAlias, isProtectedImportAlias } from './importTreeRows.js';

function ensureImportGridStyles() {
    const id = 'flow-v2-imports-panel-style';
    let style = document.getElementById(id);
    if (!style) {
        style = document.createElement('style');
        style.id = id;
        document.head.appendChild(style);
    }
    style.textContent = `
        .flow-v2-imports-panel .ag-cell-wrapper {
            display: flex;
            align-items: center;
            height: 100%;
        }
        .flow-v2-imports-panel .ag-group-value {
            display: inline-flex;
            align-items: center;
            line-height: inherit;
            overflow: hidden;
        }
        .flow-v2-imports-panel .flow-v2-import-cell {
            display: flex;
            align-items: center;
            height: 100%;
            width: 100%;
            min-width: 0;
            line-height: inherit;
        }
        .flow-v2-imports-panel .flow-v2-import-cell--name {
            display: inline-flex;
            align-items: center;
            min-width: 0;
            line-height: inherit;
        }
        .flow-v2-imports-panel .flow-v2-import-class {
            display: inline-flex;
            align-items: center;
            gap: 2px;
            min-width: 0;
            line-height: inherit;
        }
        .flow-v2-imports-panel .flow-v2-import-class--select {
            cursor: pointer;
        }
        .flow-v2-imports-panel .flow-v2-import-class .q-icon {
            font-size: 18px;
            opacity: 0.55;
        }
        .flow-v2-imports-panel .ag-cell .q-field--dense .q-field__control,
        .flow-v2-imports-panel .ag-cell .q-field--dense .q-field__marginal {
            min-height: 28px;
            height: 28px;
        }
        .flow-v2-imports-panel .ag-cell .q-field {
            width: 100%;
        }
    `;
}

function folderItemList(raw) {
    if (Array.isArray(raw))
        return raw;
    if (Array.isArray(raw?.Result))
        return raw.Result;
    if (Array.isArray(raw?.Rows))
        return raw.Rows;
    return [];
}

const ImportNameCell = defineComponent({
    name: 'flow-v2-import-name-cell',
    props: {
        params: { type: Object, default: null },
    },
    methods: {
        panel() {
            return this.params?.panel || this.params?.innerRendererParams?.panel;
        },
    },
    render() {
        const panel = this.panel();
        return panel ? panel.renderNameCell(this.params?.data) : null;
    },
});

const ImportClassCell = defineComponent({
    name: 'flow-v2-import-class-cell',
    props: {
        params: { type: Object, default: null },
    },
    methods: {
        panel() {
            return this.params?.panel;
        },
    },
    render() {
        const panel = this.panel();
        return panel ? panel.renderClassCell(this.params?.data) : null;
    },
});

const ImportActionsCell = defineComponent({
    name: 'flow-v2-import-actions-cell',
    props: {
        params: { type: Object, default: null },
    },
    methods: {
        panel() {
            return this.params?.panel;
        },
    },
    render() {
        const panel = this.panel();
        return panel ? panel.renderActionsCell(this.params?.data) : null;
    },
});

export default defineComponent({
    name: 'flow-v2-imports-panel',
    components: {
        QBtn, QIcon, QSpinner, QBanner, QDialog, QCard, QCardSection, QCardActions,
        QSelect, QBar, QToolbarTitle, QSpace, QSeparator, QInput, QMenu, QList, QItem, QItemSection, QTooltip,
        AgGridVue,
        FlowV2ListToolbar,
        ImportNameCell,
        ImportClassCell,
        ImportActionsCell,
    },
    props: {
        rows: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        error: { type: String, default: '' },
        currentProjectId: { type: String, default: '' },
        folderProjects: { type: Array, default: () => [] },
        validateAlias: { type: Function, default: null },
        suggestAlias: { type: Function, default: null },
        visible: { type: Boolean, default: true },
        readOnly: { type: Boolean, default: false },
    },
    emits: ['add', 'add-alias', 'rename-alias', 'change-alias-class', 'remove-alias', 'remove-explicit', 'drop-project'],
    data() {
        return {
            dropActive: false,
            dragDepth: 0,
            addOpen: false,
            addLoading: false,
            addProjectId: '',
            addProjects: [],
            drafts: {},
            editing: null,
            pickingClass: false,
            folderItemsExtra: [],
            searchText: '',
            gridApi: null,
            gridRowData: [],
            defaultColDef: {
                sortable: false,
                resizable: true,
                suppressHeaderMenuButton: true,
            },
            autoGroupColumnDef: {
                headerName: 'Name',
                field: 'Name',
                flex: 1,
                minWidth: 140,
                cellRenderer: 'agGroupCellRenderer',
                cellRendererParams: {
                    suppressCount: true,
                    innerRenderer: 'ImportNameCell',
                    innerRendererParams: { panel: null },
                    panel: null,
                },
            },
            importColumnDefs: [
                {
                    headerName: 'Class',
                    colId: 'Class',
                    minWidth: 100,
                    width: 148,
                    cellRenderer: 'ImportClassCell',
                    cellRendererParams: { panel: null },
                },
                {
                    headerName: '',
                    colId: 'Actions',
                    minWidth: 72,
                    maxWidth: 88,
                    cellRenderer: 'ImportActionsCell',
                    cellRendererParams: { panel: null },
                    sortable: false,
                    filter: false,
                    resizable: false,
                },
            ],
            gridComponents: {
                ImportNameCell,
                ImportClassCell,
                ImportActionsCell,
            },
        };
    },
    computed: {
        addOptions() {
            return (this.addProjects || []).map(p => ({
                label: p.Path ? `${p.Name} — ${p.Path}` : (p.Name || p.ID),
                value: p.ID,
            }));
        },
        folderItems() {
            const extra = this.folderItemsExtra || [];
            if (!extra.length)
                return this.folderProjects || [];
            const seen = new Set((this.folderProjects || []).map((p) => p.ID));
            return (this.folderProjects || []).concat(extra.filter((p) => p?.ID && !seen.has(p.ID)));
        },
        treeRows() {
            return buildImportTreeRows(this.rows, this.drafts, {
                suggestAlias: (name) => this.suggestedName({ Name: name }, defaultClassName(name)),
            });
        },
        /** Structural only — omit draft/edit text so typing does not remount the grid. */
        treeSignature() {
            const draftKeys = Object.keys(this.drafts || {}).sort().join(',');
            const parts = (this.rows || []).map((r) => {
                const aliases = (r.Aliases || [])
                    .map((a) => `${a.Alias}\0${a.ClassFullName || ''}`)
                    .join(',');
                return `${r.ProjectId}:${r.Name || ''}:${r.Source || ''}:${aliases}`;
            });
            return `${parts.join('|')}#${draftKeys}`;
        },
    },
    created() {
        ensureImportGridStyles();
        this.bindRendererPanel();
        this.gridRowData = this.treeRows;
    },
    watch: {
        treeSignature() {
            this.gridRowData = this.treeRows;
            if (this.gridApi)
                this.gridApi.setGridOption('rowData', this.gridRowData);
        },
        visible(show) {
            if (show)
                this.$nextTick(() => this.refreshGrid());
        },
    },
    methods: {
        bindRendererPanel() {
            const groupParams = this.autoGroupColumnDef.cellRendererParams;
            groupParams.panel = this;
            if (groupParams.innerRendererParams)
                groupParams.innerRendererParams.panel = this;
            this.importColumnDefs.forEach((col) => {
                if (col.cellRendererParams)
                    col.cellRendererParams.panel = this;
            });
        },
        refreshGrid() {
            if (!this.gridApi)
                return;
            this.gridRowData = this.treeRows;
            this.gridApi.setGridOption('rowData', this.gridRowData);
            this.gridApi.sizeColumnsToFit?.();
        },
        gridReady(params) {
            this.gridApi = params.api;
            this.bindRendererPanel();
            if (this.gridRowData?.length)
                this.gridApi.setGridOption('rowData', this.gridRowData);
        },
        importGetDataPath(data) {
            if (data.rowKind === 'project')
                return [data.ID];
            if (data.rowKind === 'alias')
                return [data.projectId, data.Name];
            return [data.projectId, '__draft__'];
        },
        getRowId(params) {
            return params.data.ID;
        },
        onCellClicked(params) {
            const clickedControl = !!params.event?.target?.closest?.('.q-btn, .q-menu, .q-field, input, textarea');
            if (clickedControl)
                return;
            if (params.column?.getColId?.() === 'Actions' || params.column?.getColId?.() === 'Class')
                return;
            if (params.node?.hasChildren?.())
                params.node.setExpanded(!params.node.expanded);
        },
        aliasRowId(row, alias) {
            if (!row?.ProjectId)
                return '';
            if (alias?._implied)
                return `${row.ProjectId}::implied`;
            return `${row.ProjectId}::${alias?.Alias || ''}`;
        },
        refreshRow(rowId) {
            if (!this.gridApi || !rowId)
                return;
            const node = this.gridApi.getRowNode(rowId);
            if (node)
                this.gridApi.refreshCells({ rowNodes: [node], force: true });
        },
        isLegal(alias, except) {
            if (typeof this.validateAlias === 'function')
                return this.validateAlias(alias, except);
            return !!alias && /^[A-Za-z_][A-Za-z0-9_]*$/.test(alias);
        },
        suggestedName(row, className) {
            const projectName = row?.Name || '';
            const isDef = !className || className === defaultClassName(projectName);
            const seed = isDef ? projectName : className;
            if (typeof this.suggestAlias === 'function') {
                const suggested = this.suggestAlias(seed) || this.suggestAlias(projectName);
                if (suggested)
                    return suggested;
            }
            return defaultImportAlias(seed) || defaultImportAlias(projectName);
        },
        classesFor(row) {
            return listClassesForRow(row, this.folderItems);
        },
        classOptions(row) {
            return this.classesFor(row).map((c) => ({ label: c.label, value: c.name }));
        },
        async refreshFolderItems() {
            try {
                const items = await utils.api.get('Apps/FlowV2/ListAllFolderItems');
                const list = folderItemList(items);
                if (list.length)
                    this.folderItemsExtra = list;
            }
            catch {
                // Folder tree / row.Classes are enough when the listing API is unavailable.
            }
        },
        persistImpliedDefault(row) {
            if ((row.Aliases || []).some((a) => a?.Alias && String(a.Alias).trim()))
                return;
            const name = defaultImportAlias(row.Name, (n) => (
                typeof this.suggestAlias === 'function' ? this.suggestAlias(n) : ''
            ));
            if (name && this.isLegal(name, null))
                this.$emit('add-alias', row, name, defaultClassName(row.Name));
        },
        async beginDraft(row) {
            this.persistImpliedDefault(row);
            if (this.classesFor(row).length <= 1)
                await this.refreshFolderItems();
            const id = row.ProjectId;
            const className = preferredUnusedClassName(
                { ...row, Aliases: (row.Aliases || []).length ? row.Aliases : [{ Alias: '_', ClassFullName: generatedClassFullName(row.Name, defaultClassName(row.Name)) }] },
                this.folderItems,
            );
            this.drafts = {
                ...this.drafts,
                [id]: { text: this.suggestedName(row, className), className, error: '' },
            };
            this.$nextTick(() => {
                this.gridApi?.getRowNode(id)?.setExpanded(true);
                this.focusField(`draft-${id}`);
            });
        },
        cancelDraft(id) {
            const next = { ...this.drafts };
            delete next[id];
            this.drafts = next;
        },
        commitDraft(row) {
            const id = row.ProjectId;
            const draft = this.drafts[id];
            if (!draft)
                return;
            const text = String(draft.text || '').trim() || this.suggestedName(row, draft.className);
            if (!text) {
                this.cancelDraft(id);
                return;
            }
            if (!this.isLegal(text, null)) {
                this.drafts = { ...this.drafts, [id]: { ...draft, error: 'Use a unique name (letters, digits, _)' } };
                this.refreshRow(`${id}::draft`);
                return;
            }
            this.cancelDraft(id);
            this.$emit('add-alias', row, text, draft.className || preferredUnusedClassName(row, this.folderItems));
        },
        updateDraft(id, text) {
            const draft = this.drafts[id];
            if (draft) {
                draft.text = text;
                draft.error = '';
                return;
            }
            this.drafts = { ...this.drafts, [id]: { text, className: '', error: '' } };
        },
        updateDraftClass(row, className) {
            const id = row.ProjectId;
            const draft = this.drafts[id];
            if (draft) {
                draft.className = className;
                this.refreshRow(`${id}::draft`);
                return;
            }
            this.drafts = { ...this.drafts, [id]: { text: this.suggestedName(row, className), className, error: '' } };
        },
        onAliasClassChange(row, alias, className) {
            if (this.editing
                && this.editing.projectId == row.ProjectId
                && this.editing.alias == alias.Alias) {
                const text = String(this.editing.text || '').trim();
                this.editing = null;
                this.pickingClass = false;
                if (text && text !== alias.Alias && this.isLegal(text, alias.Alias)) {
                    this.$emit('rename-alias', row, alias.Alias, text, className);
                    return;
                }
            }
            this.$emit('change-alias-class', row, alias, className);
        },
        classCell(row, className, onChange) {
            const options = this.classOptions(row);
            const label = aliasClassLabel(row?.Name, className) || 'default';
            if (options.length <= 1) {
                return this.cellWrap(
                    <span
                        class="flow-v2-import-class"
                        title={label === 'default' ? 'Default class' : `Class ${label}`}
                    >
                        {label}
                    </span>,
                );
            }
            return this.cellWrap(
                <span
                    class="flow-v2-import-class flow-v2-import-class--select"
                    title="Class this alias refers to"
                    onMousedown={() => { this.pickingClass = true; }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {label}
                    <QIcon name="mdi-menu-down" size="18px" />
                    <QMenu
                        auto-close
                        onHide={() => { this.pickingClass = false; }}
                    >
                        <QList dense>
                            {options.map((opt) => (
                                <QItem
                                    key={opt.value}
                                    clickable
                                    active={opt.value === className}
                                    onClick={() => onChange(opt.value)}
                                >
                                    <QItemSection>{opt.label}</QItemSection>
                                </QItem>
                            ))}
                        </QList>
                    </QMenu>
                </span>,
            );
        },
        cellWrap(child, kind) {
            return (
                <div class={kind === 'name' ? 'flow-v2-import-cell flow-v2-import-cell--name' : 'flow-v2-import-cell'}>
                    {child}
                </div>
            );
        },
        beginEdit(row, alias) {
            this.editing = { projectId: row.ProjectId, alias: alias.Alias, text: alias.Alias, error: '', implied: !!alias._implied };
            this.$nextTick(() => {
                this.refreshRow(this.aliasRowId(row, alias));
                this.focusField(`edit-${row.ProjectId}-${alias.Alias}`);
            });
        },
        cancelEdit() {
            const prev = this.editing;
            this.editing = null;
            if (prev)
                this.refreshRow(prev.implied ? `${prev.projectId}::implied` : `${prev.projectId}::${prev.alias}`);
        },
        commitEdit(row, alias) {
            if (!this.editing || this.editing.projectId != row.ProjectId || this.editing.alias != alias.Alias)
                return;
            const implied = !!alias._implied;
            const fallback = alias.Alias || this.suggestedName(row, classNameFromAlias(row, alias));
            const text = String(this.editing.text || '').trim() || fallback;
            if (text === alias.Alias && !implied) {
                this.cancelEdit();
                return;
            }
            if (!this.isLegal(text, implied ? null : alias.Alias)) {
                this.editing = { ...this.editing, error: 'Use a unique name (letters, digits, _)' };
                this.refreshRow(this.aliasRowId(row, alias));
                return;
            }
            this.editing = null;
            const className = classNameFromAlias(row, alias) || defaultClassName(row.Name);
            if (implied)
                this.$emit('add-alias', row, text, className);
            else
                this.$emit('rename-alias', row, alias.Alias, text);
        },
        projectsFrom(list) {
            return (list || [])
                .filter(p => p && p.Type == 'Project' && p.ID && p.ID != this.currentProjectId)
                .slice()
                .sort((a, b) => String(a.Name || '').localeCompare(String(b.Name || '')));
        },
        async openAddDialog() {
            this.addProjectId = '';
            this.addOpen = true;
            this.addLoading = true;
            const local = this.projectsFrom(this.folderProjects);
            if (local.length)
                this.addProjects = local;
            try {
                const items = await utils.api.get('Apps/FlowV2/ListAllFolderItems');
                const fromApi = this.projectsFrom(folderItemList(items));
                if (fromApi.length)
                    this.addProjects = fromApi;
                else if (!this.addProjects.length)
                    this.addProjects = local;
            }
            catch {
                if (!this.addProjects.length)
                    this.addProjects = local;
            }
            finally {
                this.addLoading = false;
            }
        },
        confirmAdd() {
            const hit = (this.addProjects || []).find(p => p.ID == this.addProjectId);
            if (!hit)
                return;
            this.addOpen = false;
            this.$emit('add', hit);
        },
        parseFolderPayload(e) {
            try {
                const raw = e.dataTransfer?.getData('application/json');
                if (!raw)
                    return null;
                const obj = JSON.parse(raw);
                if (obj.origin !== 'FlowV2FolderTree')
                    return null;
                if (obj.Type === 'Project' || obj.Type === 'APIHeader' || obj.Type === 'FlowHeader')
                    return obj;
                return null;
            }
            catch {
                return null;
            }
        },
        onDragEnter(e) {
            e.preventDefault();
            this.dragDepth += 1;
            this.dropActive = true;
            if (e.dataTransfer)
                e.dataTransfer.dropEffect = 'copy';
        },
        onDragOver(e) {
            e.preventDefault();
            if (e.dataTransfer)
                e.dataTransfer.dropEffect = 'copy';
        },
        onDragLeave() {
            this.dragDepth = Math.max(0, this.dragDepth - 1);
            if (this.dragDepth === 0)
                this.dropActive = false;
        },
        onDrop(e) {
            e.preventDefault();
            this.dragDepth = 0;
            this.dropActive = false;
            const obj = this.parseFolderPayload(e);
            if (obj)
                this.$emit('drop-project', obj);
        },
        focusField(name) {
            const el = this._focusEls?.[name];
            if (el && typeof el.focus === 'function')
                el.focus();
        },
        symbolInput(refName, value, error, onInput, onCommit, onCancel) {
            return this.cellWrap(
                <QInput
                    ref={(el) => {
                        if (!this._focusEls)
                            this._focusEls = {};
                        this._focusEls[refName] = el;
                    }}
                    dense
                    borderless
                    hide-bottom-space
                    autofocus
                    modelValue={value}
                    error={!!error}
                    error-message={error || undefined}
                    onUpdate:modelValue={onInput}
                    onClick={(e) => e.stopPropagation()}
                    onKeydown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onCommit();
                        }
                        else if (e.key === 'Escape') {
                            e.preventDefault();
                            onCancel();
                        }
                    }}
                    onBlur={() => {
                        setTimeout(() => {
                            if (this.pickingClass) {
                                this.pickingClass = false;
                                return;
                            }
                            onCommit();
                        }, 0);
                    }}
                />,
            );
        },
        renderNameCell(data) {
            if (!data)
                return null;
            if (data.rowKind === 'project') {
                const row = data.project;
                return this.cellWrap(
                    [
                        <span class="ellipsis" title={row?.Name}>{row?.Name}</span>,
                        <QMenu context-menu auto-close>
                            <QList dense>
                                <QItem clickable onClick={() => this.beginDraft(row)}>
                                    <QItemSection>Add alias</QItemSection>
                                </QItem>
                                {row?.Source !== 'Canvas' ? (
                                    <QItem clickable onClick={() => this.$emit('remove-explicit', row)}>
                                        <QItemSection>Remove import</QItemSection>
                                    </QItem>
                                ) : null}
                            </QList>
                        </QMenu>,
                    ],
                    'name',
                );
            }
            if (data.rowKind === 'draft') {
                const row = data.project;
                const id = data.projectId;
                const draft = this.drafts[id];
                return this.symbolInput(
                    `draft-${id}`,
                    draft?.text || '',
                    draft?.error || '',
                    (v) => this.updateDraft(id, v),
                    () => this.commitDraft(row),
                    () => this.cancelDraft(id),
                );
            }
            const row = data.project;
            const alias = data.alias;
            const id = data.projectId;
            const editing = this.editing
                && this.editing.projectId == id
                && this.editing.alias == alias.Alias;
            if (editing) {
                return this.symbolInput(
                    `edit-${id}-${alias.Alias}`,
                    this.editing.text,
                    this.editing.error,
                    (v) => {
                        if (this.editing) {
                            this.editing.text = v;
                            this.editing.error = '';
                        }
                    },
                    () => this.commitEdit(row, alias),
                    this.cancelEdit,
                );
            }
            const protectedAlias = isProtectedImportAlias(row, alias);
            return this.cellWrap(
                [
                    <span
                        style={{ cursor: 'text' }}
                        title="Click to rename the alias used in API code"
                        onClick={(e) => { e.stopPropagation(); this.beginEdit(row, alias); }}
                    >
                        {alias.Alias}
                    </span>,
                    <QMenu context-menu auto-close>
                        <QList dense>
                            <QItem clickable onClick={() => this.beginEdit(row, alias)}>
                                <QItemSection>Rename alias</QItemSection>
                            </QItem>
                            {!protectedAlias ? (
                                <QItem clickable onClick={() => this.$emit('remove-alias', row, alias)}>
                                    <QItemSection>Remove alias</QItemSection>
                                </QItem>
                            ) : null}
                        </QList>
                    </QMenu>,
                ],
                'name',
            );
        },
        renderClassCell(data) {
            if (!data || data.rowKind === 'project')
                return null;
            const row = data.project;
            if (data.rowKind === 'draft') {
                const draft = this.drafts[data.projectId];
                return this.classCell(
                    row,
                    draft?.className || preferredUnusedClassName(row, this.folderItems),
                    (v) => this.updateDraftClass(row, v),
                );
            }
            return this.classCell(
                row,
                classNameFromAlias(row, data.alias),
                (v) => this.onAliasClassChange(row, data.alias, v),
            );
        },
        renderActionsCell(data) {
            if (!data)
                return null;
            if (data.rowKind === 'project') {
                const row = data.project;
                const canvas = row?.Source === 'Canvas';
                return this.cellWrap(
                    <div class="row no-wrap items-center justify-end" style={{ width: '100%' }}>
                        <QBtn
                            flat
                            dense
                            round
                            icon="mdi-plus"
                            size="sm"
                            title="Add alias"
                            onClick={(e) => { e.stopPropagation(); this.beginDraft(row); }}
                        />
                        {!canvas ? (
                            <QBtn
                                flat
                                dense
                                round
                                icon="mdi-close"
                                size="sm"
                                title="Remove import"
                                onClick={(e) => { e.stopPropagation(); this.$emit('remove-explicit', row); }}
                            />
                        ) : null}
                    </div>,
                );
            }
            if (data.rowKind === 'alias') {
                if (isProtectedImportAlias(data.project, data.alias))
                    return null;
                return this.cellWrap(
                    <div class="row no-wrap items-center justify-end" style={{ width: '100%' }}>
                        <QBtn
                            flat
                            dense
                            round
                            icon="mdi-close"
                            size="sm"
                            title="Remove alias"
                            onClick={(e) => { e.stopPropagation(); this.$emit('remove-alias', data.project, data.alias); }}
                        />
                    </div>,
                );
            }
            return null;
        },
    },
    render() {
        if (this.loading) {
            return (
                <div class="q-pa-md flex flex-center">
                    <QSpinner color="primary" size="32px" />
                </div>
            );
        }

        return (
            <div
                class="flow-v2-imports-panel"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: 0,
                    outline: this.dropActive ? '2px dashed #1976d2' : '2px solid transparent',
                    outlineOffset: -2,
                    background: this.dropActive ? 'rgba(25,118,210,0.06)' : undefined,
                    pointerEvents: this.readOnly ? 'none' : undefined,
                }}
                onDragenter={this.onDragEnter}
                onDragover={this.onDragOver}
                onDragleave={this.onDragLeave}
                onDrop={this.onDrop}
            >
                <FlowV2ListToolbar
                    modelValue={this.searchText}
                    onUpdate:modelValue={(v) => { this.searchText = v || ''; }}
                >
                    <QBtn
                        flat
                        round
                        dense
                        icon="mdi-plus"
                        color="blue-grey-8"
                        title="Add a project if drag-drop is not available"
                        onClick={this.openAddDialog}
                    >
                        <QTooltip>Add a project if drag-drop is not available</QTooltip>
                    </QBtn>
                </FlowV2ListToolbar>
                {this.error ? <QBanner class="bg-red-1 text-negative q-mx-sm q-mb-sm">{this.error}</QBanner> : null}
                {(this.rows || []).length === 0 ? (
                    <div class="text-grey q-pa-sm">
                        Drag a project from the folder tree. Then set the symbol you will type, like <span style={{ fontFamily: 'monospace' }}>Alias.Method</span>.
                    </div>
                ) : (
                    <div
                        class="ag-grid-noborder"
                        style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' }}
                    >
                        <AgGridVue
                            style={{ flex: '1 1 0', minHeight: 0, width: '100%' }}
                            headerHeight={30}
                            rowHeight={33}
                            treeData={true}
                            groupDefaultExpanded={1}
                            animateRows={false}
                            autoGroupColumnDef={this.autoGroupColumnDef}
                            defaultColDef={this.defaultColDef}
                            columnDefs={this.importColumnDefs}
                            rowData={this.gridRowData}
                            getDataPath={this.importGetDataPath}
                            onCellClicked={this.onCellClicked}
                            components={this.gridComponents}
                            cellSelection={false}
                            quickFilterText={this.searchText}
                            getRowId={this.getRowId}
                            onGridReady={this.gridReady}
                        />
                    </div>
                )}
                <QDialog
                    modelValue={this.addOpen}
                    onUpdate:modelValue={(v) => { this.addOpen = v; }}
                    persistent
                >
                    <QCard style={{ width: '480px', maxWidth: 'calc(100vw - 24px)' }}>
                        <QBar class="bg-primary text-white">
                            <QToolbarTitle>Add sibling project</QToolbarTitle>
                            <QSpace />
                            <QBtn flat round dense icon="mdi-close" onClick={() => { this.addOpen = false; }} />
                        </QBar>
                        <QCardSection class="q-gutter-md">
                            <div class="text-caption text-grey">
                                Prefer dragging a project from the folder tree onto Imports.
                            </div>
                            {this.addLoading ? (
                                <div class="row justify-center q-pa-md">
                                    <QSpinner color="primary" size="28px" />
                                </div>
                            ) : (
                                <QSelect
                                    outlined
                                    dense
                                    emit-value
                                    map-options
                                    options-dense
                                    label="Project"
                                    options={this.addOptions}
                                    modelValue={this.addProjectId}
                                    onUpdate:modelValue={(v) => { this.addProjectId = v; }}
                                />
                            )}
                        </QCardSection>
                        <QSeparator />
                        <QCardActions align="right">
                            <QBtn
                                unelevated
                                color="primary"
                                label="OK"
                                disable={!this.addProjectId}
                                onClick={this.confirmAdd}
                            />
                        </QCardActions>
                    </QCard>
                </QDialog>
            </div>
        );
    },
});
